import { t } from 'i18next';
import z from 'zod';
import { disabledFlag } from '../../../lib/schemas/disabled-flag';
import { numericString, optionalNumericString } from '../../../lib/schemas/numeric-string';
import { slugStartingWithLetter } from '../../../lib/schemas/slugs';
import { CUSTOMER_GROUP_NAME_MAX_LENGTH, CUSTOMER_GROUP_SLUG_MAX_LENGTH } from '../constants';

const SettingPriceListSchema = z
  .object({
    price_list_slug: z.string(),
  })
  .array()
  .superRefine((priceLists, ctx) => {
    const priceListsSet = new Set();
    const errorMsgs = [];
    let hasEmptyError = false;
    for (const { price_list_slug } of priceLists) {
      if (priceListsSet.has(price_list_slug)) {
        errorMsgs.push(t('features:customer_group.components.price_lists_card.errors.duplicate'));
        break;
      }

      if (price_list_slug === '') {
        if (!hasEmptyError) {
          errorMsgs.push(t('features:customer_group.components.price_lists_card.errors.empty'));
          hasEmptyError = true;
        }
      } else {
        priceListsSet.add(price_list_slug);
      }
    }

    if (errorMsgs.length > 0) {
      ctx.addIssue({
        code: 'custom',
        message: errorMsgs.join('\n'),
      });
    }
  });

export type SettingPriceListState = z.input<typeof SettingPriceListSchema>;

const SettingsOrderTotalLimitSchema = z
  .object({
    currency_code: z.string(),
    minimum_value: optionalNumericString({ min: 0, emptyAsUndefined: true }),
    maximum_value: optionalNumericString({ min: 0, emptyAsUndefined: true }),
    compare_type: z.string(),
  })
  .superRefine((limit, ctx) => {
    if (
      limit?.minimum_value !== undefined &&
      limit?.maximum_value !== undefined &&
      limit.maximum_value <= limit.minimum_value
    ) {
      ctx.addIssue({
        code: 'custom',
        message: t('errors:forms.number.min_exclusive', { min: limit.minimum_value }),
        path: ['maximum_value'],
      });
    }
  });

const SettingOrderTotalLimitsSchema = SettingsOrderTotalLimitSchema.array().superRefine(
  (limits, ctx) => {
    const LimitsSet = new Set();
    for (const { currency_code } of limits) {
      if (LimitsSet.has(currency_code)) {
        ctx.addIssue({
          code: 'custom',
          message: t(
            'features:customer_group.components.order_total_limits_card.errors.duplicate_currency_codes',
          ),
        });
        return;
      }
      LimitsSet.add(currency_code);
    }
  },
);

export type SettingOrderTotalLimitsState = z.input<typeof SettingOrderTotalLimitsSchema>;
export type SettingOrderTotalLimitsValidated = z.output<typeof SettingOrderTotalLimitsSchema>;

export const SettingOrderQuantityLimitsSchema = z
  .object({
    minimum_quantity: optionalNumericString({ min: 1, emptyAsUndefined: true, isWhole: true }),
    maximum_quantity: optionalNumericString({ min: 0, emptyAsUndefined: true, isWhole: true }),
  })
  .superRefine((limit, ctx) => {
    if (
      limit?.minimum_quantity !== undefined &&
      limit?.maximum_quantity !== undefined &&
      limit.maximum_quantity <= limit.minimum_quantity
    ) {
      ctx.addIssue({
        code: 'custom',
        message: t('errors:forms.number.min_exclusive', { min: limit.minimum_quantity }),
        path: ['maximum_quantity'],
      });
    }
  });

export type SettingOrderQuantityLimitsState = z.input<typeof SettingOrderQuantityLimitsSchema>;
export type SettingOrderQuantityLimitsValidated = z.output<typeof SettingOrderQuantityLimitsSchema>;

export const SettingPaymentMethodsSchema = z.object({
  payment_by_invoice: z.boolean(),
  payment_on_account: z.boolean(),
  upfront_payment: z.boolean(),
  quote: z.boolean(),
});

export type SettingPaymentMethodsState = z.input<typeof SettingPaymentMethodsSchema>;

export const SettingAddressManagementBaseSchema = z.object({
  allowAddressEditing: z.boolean(),
  allowDefaultBillingModification: z.boolean(),
});

const SettingAddressManagementSchema = SettingAddressManagementBaseSchema.transform((val) => {
  return val.allowAddressEditing;
});

export type SettingAddressManagementState = z.input<typeof SettingAddressManagementSchema>;

export const SettingStockDisplaySchema = z.object({
  show_status: z.boolean(),
  hide_preorder: z.boolean().optional(),
  show_units: z.boolean(),
  max: numericString({ min: 0, isWhole: true }),
  low: numericString({ min: 0, isWhole: true }),
  last: numericString({ min: 0, isWhole: true }),
});

export type SettingStockDisplayState = z.input<typeof SettingStockDisplaySchema>;

export type SettingStockDisplayValidated = z.output<typeof SettingStockDisplaySchema>;

const SettingQuotingEngineSchema = z.object({
  customer_create_quote: z.boolean(),
  disabled: disabledFlag(),
});

export type SettingQuotingEngineState = z.input<typeof SettingQuotingEngineSchema>;

const SettingMultiStockSchema = z.object({
  useDefault: z.boolean(),
  allowedStockLocationIds: z.array(z.string()),
  disabled: disabledFlag(),
});

export type SettingMultiStockState = z.input<typeof SettingMultiStockSchema>;

/** Full settings shape – all fields required (API response / validated shape). */
const SettingSchema = z.object({
  priceListSelection: SettingPriceListSchema,
  allowedPaymentMethods: SettingPaymentMethodsSchema,
  quotingEngine: SettingQuotingEngineSchema,
  orderItemQuantityValidation: SettingOrderQuantityLimitsSchema,
  orderTotalsValidation: SettingOrderTotalLimitsSchema,
  allowAddressEditing: SettingAddressManagementSchema,
  allowDefaultBillingModification: z.boolean(),
  paymentOnAccountEnforceCreditLimit: z.boolean(),
  multiLocationStockConfig: SettingMultiStockSchema,
  useMultiLocationStock: z.boolean(),
  stockDisplay: SettingStockDisplaySchema,
  allowCheckout: z.boolean(),
});

export type SettingResponseState = z.input<typeof SettingSchema>;

/** Builder form: same shape as SettingSchema with every field optional. */
const CustomerGroupBuilderSettingsSchema = SettingSchema.partial().transform((settings) => {
  // A card flags `disabled` when the merchant's configuration doesn't support the feature
  const setting = { ...settings };

  if (setting.quotingEngine?.disabled) {
    delete setting.quotingEngine;
  }

  if (setting.multiLocationStockConfig?.disabled) {
    delete setting.multiLocationStockConfig;
  }

  return setting;
});

// This would be the settings, and not the customer groups
export const customerGroupBuilderSchema = z.object({
  name: z
    .string()
    .nonempty()
    .max(
      CUSTOMER_GROUP_NAME_MAX_LENGTH,
      t('errors:forms.string.max', { max: CUSTOMER_GROUP_NAME_MAX_LENGTH }),
    ),
  slug: slugStartingWithLetter({ maxLength: CUSTOMER_GROUP_SLUG_MAX_LENGTH }),
  parent_slug: slugStartingWithLetter({ maxLength: CUSTOMER_GROUP_SLUG_MAX_LENGTH }),
  hasChildren: z.boolean(),
  settings: CustomerGroupBuilderSettingsSchema,
});

export type CustomerGroupBuilderState = z.input<typeof customerGroupBuilderSchema>;
export type CustomerGroupBuilderSettingsState = z.input<typeof CustomerGroupBuilderSettingsSchema>;

export type CustomerGroupBuilderValidated = z.output<typeof customerGroupBuilderSchema>;
export type CustomerGroupBuilderSettingsValidated = z.output<
  typeof CustomerGroupBuilderSettingsSchema
>;
